<template>
  <div
    class="multiple-choice-component-container"
    :class="{ 'no-borders': leftColumn }"
  >
    <template
      v-if="!leftColumn"
    >
      <key-capture
        v-if="!answered && !disabled"
        @keydown="handleKeyDown"
      ></key-capture>
      <flashcard-instruction
        :flashcard-instruction="flashcardInstruction"
      ></flashcard-instruction>
      <template v-for="(answer, index) in answers" :key="answer.id">
        <input
          :id="`answer-${answer.id}`"
          v-model="selected"
          :disabled="answered || disabled"
          :value="answer.id"
          type="checkbox"
        >
        <label
          :for="`answer-${answer.id}`"
          :class="{
            selected: selected.includes(answer.id),
            answered: answered,
            strike: strikes.includes(answer.id) && !answered,
            correct: answered && answer.correct, box: allowMultipleAnswers,
            highlighted: highlightedAnswerId === answer.id,
          }"
          :tabindex="answered ? -1 : 0"
          role="checkbox"
          :aria-checked="selected.includes(answer.id)"
          :aria-disabled="answered"
          @click="handleClick(answer.id)"
          @keydown.space.self="selectAnswer(answer.id)"
          @keydown.enter.self="selectAnswer(answer.id)"
        >
          <radio-button
            :answered="answered"
            :selected="selected.includes(answer.id)"
            :correct="answered && answer.correct"
            :use-answer-labels="useAnswerLabels"
            :is-sata="allowMultipleAnswers"
            :index="index"
            class="radio-button-adjustment"
          ></radio-button>
          <div class="text">
            <span
              class="answer-text"
              :class="{
                highlighted: highlightedAnswerId === answer.id,
              }"
              v-html="answer.content"
            ></span>
          </div>
          <button
            v-if="!answered"
            class="strike-button"
            @click="strike(answer.id, $event)"
          >
            <icon
              class="colored-icon"
              :class="{'rotated-icon': strikes.includes(answer.id)}"
              name="cross-thin"
              variant="9aa4b2"
            />
          </button>
        </label>
      </template>
    </template>
    <answer-selection-list
      v-else
      :answers="answers"
      :disable-select="false"
      :selected="answerIds"
      :flashcard="flashcard"
      :is-sata="allowMultipleAnswers"
      :suppress-answer-labels="false"
      @select-answer="handleClick"
    ></answer-selection-list>
  </div>
</template>

<script>
import Icon from '@/components/singletons/icon';
import RadioButton from '@/components/flashcards-v2/fragments/RadioButton';
import KeyCapture from '@/components/singletons/KeyCapture.vue';
import AnswerSelectionList from '@/components/flashcards-v2/fragments/AnswerSelectionList.vue';
import FlashcardInstruction from '@/components/flashcards-v2/fragments/FlashcardInstruction.vue';
import MultipleChoiceMixin from '@/mixins/multipleChoice.js';

export default {
  components: { Icon, KeyCapture, RadioButton, AnswerSelectionList, FlashcardInstruction },
  mixins: [MultipleChoiceMixin],
  props: {
    answered: {
      type: Boolean,
      default: false,
    },
    answerIds: {
      type: Array,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    flashcard: {
      type: Object,
      default: () => ({}),
    },
    flashcardState: {
      type: Object,
      default: () => ({}),
    },
    leftColumn: {
      type: Boolean,
      default: false,
    },
    highlightedAnswerId: {
      type: Number,
      default: null,
    },
  },
  emits: ['set-answer-ids', 'click'],
  data() {
    return {
      strikes: [],
    };
  },
  computed: {
    selected: {
      get() {
        if (this.flashcardState.answerIds) {
          return this.flashcardState.answerIds;
        }
        return this.answerIds;
      },
      set(value) {
        const changed = value[value.length - 1];
        if(!this.allowMultipleAnswers && value.length > 0) {
          this.$emit('set-answer-ids', [changed]);
        } else {
          this.$emit('set-answer-ids', value);
        }
        let index = this.strikes.findIndex(a => a == changed);
        if(index >= 0) {
          this.strikes.splice(index, 1);
        }
      },
    },
    allowMultipleAnswers() {
      return this.flashcard.answers.filter(a => a.correct).length > 1 || this.flashcard.type === 'SATA';
    },
    flashcardInstruction() {
      if (this.allowMultipleAnswers && !this.leftColumn) {
        return 'Select all that apply';
      }
      return '';
    },
    useAnswerLabels() {
      return this.$store.getters['app/answerLabelsEnabled'];
    },
  },
  methods: {
    handleClick(answerId) {
      if (this.answered) {
        this.$emit('click', answerId);
        return;
      }
    },
    handleKeyDown(key) {
      if (key.length !== 1) {
        return;
      }
      if (this.answered || this.disabled) {
        return;
      }
      const index = parseInt(key) - 1;
      if (typeof index === 'number' && isNaN(index)) {
        return;
      }
      if (index < 0 || index >= this.answers.length) {
        return;
      }
      this.selectAnswer(this.answers[index].id);
    },
    selectAnswer(answerId) {
      if (this.answered) {
        // An added check to make sure that answers aren't selectable after the question has been answered
        return;
      }
      if (this.answerIds.includes(answerId)) {
        this.$emit('set-answer-ids', this.answerIds.filter(id => id !== answerId));
      } else {
        if (this.allowMultipleAnswers) {
          this.$emit('set-answer-ids', [...this.answerIds, answerId]);
        } else {
          this.$emit('set-answer-ids', [answerId]);
        }
      }

      if (this.strikes.includes(answerId)) {
        this.strikes = this.strikes.filter(id => id !== answerId);
      }
    },
    strike(value, e) {
      e.preventDefault();
      if(!this.answered) {
        if(!this.strikes.includes(value)) {
          this.strikes.push(value);
        } else {
          let index = this.strikes.findIndex(a => a == value);
          this.strikes.splice(index, 1);
        }
        if(this.selected.includes(value)) {
          this.selected = this.selected.filter(id => id != value);
        }
      }
    },
  },
};
</script>

<style lang='scss' scoped>
  .multiple-choice-component-container {
    display: flex;
    flex-direction: column;
    gap: rem-calc(12);
    width: 100%;

    &.no-borders {
      input {
        + label {
          border: none !important;
          background-color: transparent !important;
          cursor: pointer;

          &.highlighted {
            background-color: #EFF4FF !important;
            border-radius: 8px;
          }

          .answer-text {
            font-weight: 600;
            color: #697586;

            &.highlighted {
              color: #155EEF;
            }
          }
        }
      }
    }

    input {
      display: none;

      + label {
        border: 1px solid #cdd5df;
        display: flex;
        align-items: flex-start;
        padding: rem-calc(8);
        border-radius: rem-calc(6);
        box-sizing: border-box;

        @include focus-not-visible {
          outline: none;
        }

        &:hover:not(.answered),
        &.selected {
          background-color: #EFF4FF;
          border: 2px solid #155EEF;
          padding: rem-calc(7);

          &.answered {
            border: 1px solid #F04438;
            background-color: #FEF3F2;
            padding: rem-calc(8);

            &.correct {
              border: 1px solid #17B26A;
              background-color: #ECFDF3;
              padding: rem-calc(8);
            }
          }
        }

        &:not(.answered) {
          cursor: pointer;
        }

        &:hover:not(.answered) {
          background-color: #EFF4FF;
          border: 2px solid #155EEF;
          padding: rem-calc(7);
        }

        .selected-check {
          display: none;
        }

        .text {
          color: #232323;
          flex: 1;
          line-height: rem-calc(24);
          margin: 0 rem-calc(12);

          .answer-text {
            :deep(p) {
              margin: 0;
            }

            :deep(img) {
              box-sizing: border-box;
              max-height: rem-calc(560);
            }
          }

          .answer-percentage {
            font-size: rem-calc(12);
            font-weight: 600;
            color: rgba(105, 117, 134, 1);
          }

          .rationale-text {
            color: #767676;
            font-size: rem-calc(14);
            line-height: rem-calc(21);
            padding: rem-calc(8) 0;
            &.locked {
              filter: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg"><filter id="filter"><feGaussianBlur stdDeviation="5" /></filter></svg>#filter');
              filter: blur(4px);
            }
            :deep(p) {
              margin: 0;
            }
          }
        }

        .strike-button {
          padding: 0;
          border-radius: 100%;
          background-color: transparent;
          height: rem-calc(18);
          margin: rem-calc(4) 0 rem-calc(4) auto;
          width: rem-calc(18);

          img {
            margin-bottom: rem-calc(2);
            width: rem-calc(8);
          }
        }

        &.strike {
          .strike-button .rotated-icon {
            transform: rotate(45deg);
          }

          .answer-text {
            text-decoration: line-through;
          }
        }
      }
    }

    .radio-button-adjustment {
      margin-top: rem-calc(6);
    }
  }
</style>
